import axios, { isAxiosError } from 'axios';

import { useAuthStore, SHARED_TOKEN_KEY } from '@/stores/authStore';
import { parseJwtPayload } from '@/lib/auth';

const API_BASE = import.meta.env.VITE_API_URL || '';
const REFRESH_LOCK = 'kp-auth-refresh';

/** Refresh answered 401: the session is genuinely over and the user must sign in again. */
export class SessionExpiredError extends Error {
  constructor() {
    super('session expired');
    this.name = 'SessionExpiredError';
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function secondsLeft(token: string | null | undefined): number {
  if (!token) return -1;
  const exp = parseJwtPayload(token)?.exp;
  return typeof exp === 'number' ? exp - Date.now() / 1000 : -1;
}

/** The newest access token any tab obtained, if it is still comfortably valid. */
export function sharedToken(minSeconds = 30): string | null {
  try {
    const t = localStorage.getItem(SHARED_TOKEN_KEY);
    return t && secondsLeft(t) > minSeconds ? t : null;
  } catch {
    return null;
  }
}

// One POST, retried on anything that is not a definitive 401. A network
// error, timeout, or 5xx (API restarting during a deploy, laptop waking with
// no Wi-Fi yet) says nothing about whether the session is valid — the
// refresh cookie is untouched by those — so it must never log the user out.
async function postRefresh(): Promise<string> {
  let lastError: unknown;
  for (const delay of [0, 1_000, 3_000]) {
    if (delay) await sleep(delay);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/v1/auth/refresh`,
        {},
        { withCredentials: true, timeout: 15_000 },
      );
      return data.access_token as string;
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 401) throw new SessionExpiredError();
      lastError = err;
    }
  }
  throw lastError;
}

// Runs with the cross-tab lock held. Every tab shares one refresh cookie and
// each rotation revokes the token it presented, so tabs must not refresh in
// parallel: whoever gets the lock second reuses the token the first obtained.
async function refreshExclusive(staleToken: string | null): Promise<string> {
  const shared = sharedToken();
  if (shared && shared !== staleToken) return shared;
  return postRefresh();
}

let inflight: Promise<string> | null = null;

/**
 * Obtains a fresh access token. Concurrent callers in this tab share one
 * attempt; other tabs are serialised through a Web Lock. Rejects with
 * SessionExpiredError only when the server says the session is over; any other
 * rejection is transient and the caller should keep the user signed in.
 */
export function refreshAccessToken(): Promise<string> {
  inflight ??= (async () => {
    const stale = useAuthStore.getState().accessToken;
    const run = () => refreshExclusive(stale);
    const token =
      typeof navigator !== 'undefined' && navigator.locks
        ? await navigator.locks.request(REFRESH_LOCK, run)
        : await run();
    useAuthStore.getState().setAuth(token);
    return token;
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Signs the user out locally and sends them to the login page. */
export function endSession(): void {
  useAuthStore.getState().clearAuth();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

function refreshInBackground(): void {
  refreshAccessToken().catch((err) => {
    if (err instanceof SessionExpiredError) endSession();
    // Transient: leave the user signed in; the next request or the next
    // visibility change tries again.
  });
}

const EARLY_REFRESH_SECONDS = 60;
let timer: ReturnType<typeof setTimeout> | undefined;

function schedule(token: string | null): void {
  clearTimeout(timer);
  if (!token) return;
  const ms = Math.max((secondsLeft(token) - EARLY_REFRESH_SECONDS) * 1000, 0);
  timer = setTimeout(refreshInBackground, ms);
}

/**
 * Keeps the access token fresh instead of waiting for a request to 401.
 * Students sit on one page for a long time (a lesson, a long doubt, a hard
 * problem); without this the first action after 15 minutes hit an expired
 * token, and paths outside the axios interceptor (the tutor chat's fetch, the
 * submission WebSocket) simply failed.
 */
export function startProactiveRefresh(): () => void {
  schedule(useAuthStore.getState().accessToken);
  const unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (state.accessToken !== prev.accessToken) schedule(state.accessToken);
  });
  // Timers are throttled in background tabs and frozen during sleep, so
  // re-check whenever the tab becomes visible again.
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    const token = useAuthStore.getState().accessToken;
    if (token && secondsLeft(token) < EARLY_REFRESH_SECONDS + 30) refreshInBackground();
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    clearTimeout(timer);
    unsubscribe();
    document.removeEventListener('visibilitychange', onVisible);
  };
}
